/**
 * Enhanced Order Placement/Edit Page
 * Comprehensive form for creating and editing delivery orders
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import tasksService from '../../services/tasks';
import branchService from '../../services/branches';
import LocationPicker from '../../Components/LocationPicker/LocationPicker';
import './AddTaskEnhanced.css';

const AddTaskEnhanced = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode
  const isEditMode = Boolean(id);

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTask, setLoadingTask] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [createdJobId, setCreatedJobId] = useState('');
  const [showDeliveryMap, setShowDeliveryMap] = useState(false);

  const [formData, setFormData] = useState({
    // Pickup Information
    pickupType: 'branch', // 'branch' or 'custom'
    pickupBranch: '',
    pickupName: '',
    pickupAddress: '',
    pickupCity: '',
    pickupState: '',
    pickupZipCode: '',
    pickupLatitude: '',
    pickupLongitude: '',
    pickupPhone: '',

    // Delivery Information
    deliveryName: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryZipCode: '',
    deliveryLatitude: '',
    deliveryLongitude: '',
    deliveryPhone: '',
    deliveryEmail: '',

    // Order Details
    priority: 'Medium',
    signatureRequired: false,
    notes: '',
    deliveryInstructions: '',
  });

  const fetchBranches = async () => {
    try {
      const data = await branchService.getBranches({ isActive: true });
      setBranches(data);
    } catch (err) {
      console.error('Failed to load branches:', err);
    }
  };

  const loadTaskData = useCallback(async () => {
    try {
      setLoadingTask(true);
      const task = await tasksService.getTaskById(id);

      // Check if task can be edited
      const editableStatuses = ['Pending', 'Assigned'];
      if (!editableStatuses.includes(task.status)) {
        setError(`Cannot edit order. Order has status "${task.status}" and can only be edited when Pending or Assigned.`);
        setLoadingTask(false);
        return;
      }

      // Parse address to extract components
      const parseAddress = (addressStr) => {
        const parts = addressStr.split(',').map(p => p.trim());
        if (parts.length >= 3) {
          const stateZip = parts[parts.length - 1].trim().split(' ');
          return {
            street: parts.slice(0, -2).join(', '),
            city: parts[parts.length - 2],
            state: stateZip[0] || '',
            zipCode: stateZip[1] || ''
          };
        }
        return { street: addressStr, city: '', state: '', zipCode: '' };
      };

      const fromParsed = parseAddress(task.from?.address || '');
      const toParsed = parseAddress(task.to?.address || '');

      // Parse notes to extract delivery instructions
      const notesArray = task.notes ? task.notes.split(' | ') : [];
      const deliveryInstr = notesArray.find(n => n.startsWith('Delivery Instructions:'))?.replace('Delivery Instructions:', '').trim() || '';
      const baseNotes = notesArray.filter(n => !n.startsWith('Delivery Instructions:') && !n.startsWith('Delivery Contact:')).join(' | ');

      setFormData({
        pickupType: 'custom', // Default to custom for existing orders
        pickupBranch: '',
        pickupName: task.from?.name || '',
        pickupAddress: fromParsed.street,
        pickupCity: fromParsed.city,
        pickupState: fromParsed.state,
        pickupZipCode: fromParsed.zipCode,
        pickupLatitude: task.from?.coordinates?.latitude?.toString() || '',
        pickupLongitude: task.from?.coordinates?.longitude?.toString() || '',
        pickupPhone: task.from?.phone || '',

        deliveryName: task.to?.name || '',
        deliveryAddress: toParsed.street,
        deliveryCity: toParsed.city,
        deliveryState: toParsed.state,
        deliveryZipCode: toParsed.zipCode,
        deliveryLatitude: task.to?.coordinates?.latitude?.toString() || '',
        deliveryLongitude: task.to?.coordinates?.longitude?.toString() || '',
        deliveryPhone: task.to?.phone || '',
        deliveryEmail: '',

        priority: task.priority || 'Medium',
        signatureRequired: !!task.signatureRequired,
        notes: baseNotes,
        deliveryInstructions: deliveryInstr,
      });

      // Show delivery map if coordinates exist
      if (task.to?.coordinates?.latitude && task.to?.coordinates?.longitude) {
        setShowDeliveryMap(true);
      }

    } catch (err) {
      setError(err.message || 'Failed to load order details');
    } finally {
      setLoadingTask(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBranches();
    if (isEditMode) {
      loadTaskData();
    }
  }, [isEditMode, loadTaskData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-fill pickup info when branch is selected
    if (name === 'pickupBranch' && value) {
      const selectedBranch = branches.find(b => b._id === value);
      if (selectedBranch) {
        setFormData(prev => ({
          ...prev,
          pickupName: selectedBranch.name,
          pickupAddress: selectedBranch.address.street,
          pickupCity: selectedBranch.address.city,
          pickupState: selectedBranch.address.state,
          pickupZipCode: selectedBranch.address.zipCode,
          pickupLatitude: selectedBranch.latitude?.toString() || '',
          pickupLongitude: selectedBranch.longitude?.toString() || '',
          pickupPhone: selectedBranch.contact.phone,
        }));
      }
    }
  };

  const handlePickupLocationChange = useCallback((lat, lng) => {
    setFormData(prev => ({
      ...prev,
      pickupLatitude: lat.toFixed(6),
      pickupLongitude: lng.toFixed(6),
    }));
  }, []);

  const handleDeliveryLocationChange = useCallback((lat, lng) => {
    setFormData(prev => ({
      ...prev,
      deliveryLatitude: lat.toFixed(6),
      deliveryLongitude: lng.toFixed(6),
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.pickupAddress || !formData.deliveryAddress || !formData.deliveryName) {
        throw new Error('Please fill in all required fields');
      }

      // Prepare task data
      const taskData = {
        from: {
          name: formData.pickupName || formData.pickupAddress,
          address: `${formData.pickupAddress}, ${formData.pickupCity}, ${formData.pickupState} ${formData.pickupZipCode}`,
          phone: formData.pickupPhone || null,
          coordinates: {
            latitude: formData.pickupLatitude ? parseFloat(formData.pickupLatitude) : undefined,
            longitude: formData.pickupLongitude ? parseFloat(formData.pickupLongitude) : undefined,
          }
        },
        to: {
          name: formData.deliveryName,
          address: `${formData.deliveryAddress}, ${formData.deliveryCity}, ${formData.deliveryState} ${formData.deliveryZipCode}`,
          phone: formData.deliveryPhone || null,
          coordinates: {
            latitude: formData.deliveryLatitude ? parseFloat(formData.deliveryLatitude) : undefined,
            longitude: formData.deliveryLongitude ? parseFloat(formData.deliveryLongitude) : undefined,
          }
        },
        priority: formData.priority,
        signatureRequired: formData.signatureRequired,
        notes: [
          formData.notes,
          formData.deliveryInstructions ? `Delivery Instructions: ${formData.deliveryInstructions}` : '',
        ].filter(Boolean).join(' | '),
      };

      if (isEditMode) {
        // Update existing task
        await tasksService.updateTask(id, taskData);
        alert('Order updated successfully!');
        navigate('/tasks');
      } else {
        // Create new task
        taskData.scheduledDate = new Date().toISOString();
        const task = await tasksService.createTask(taskData);
        setCreatedJobId(task.jobId);

        // Generate QR code
        const qrData = await tasksService.generateQRCode(task._id);
        setQrCodeUrl(qrData.qrImageUrl);
        setQrCode(qrData.qrCode);

        // Show QR modal
        setShowQRModal(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to ${isEditMode ? 'update' : 'create'} order`);
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} order:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowQRModal(false);
    navigate('/tasks');
  };

  if (loadingTask) {
    return (
      <div className="add-task-enhanced-page">
        <div style={{ textAlign: 'center', padding: '60px', color: '#6366f1' }}>
          Loading order details...
        </div>
      </div>
    );
  }

  return (
    <div className="add-task-enhanced-page compact-layout">
      <div className="page-header compact">
        <h1>{isEditMode ? 'Edit Order' : 'Create New Order'}</h1>
        <div className="header-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/tasks')}>
            Cancel
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="order-form compact">
        {/* Two Column Layout for Pickup and Delivery */}
        <div className="form-columns">
          {/* Pickup Information Column */}
          <div className="form-column">
            <h2 className="section-title compact">Pickup</h2>

            {!isEditMode && (
              <div className="form-row">
                <label className="radio-inline">
                  <input
                    type="radio"
                    name="pickupType"
                    value="branch"
                    checked={formData.pickupType === 'branch'}
                    onChange={handleChange}
                  />
                  Branch
                </label>
                <label className="radio-inline">
                  <input
                    type="radio"
                    name="pickupType"
                    value="custom"
                    checked={formData.pickupType === 'custom'}
                    onChange={handleChange}
                  />
                  Custom
                </label>
              </div>
            )}

            {formData.pickupType === 'branch' && !isEditMode && (
              <div className="form-group compact">
                <select
                  name="pickupBranch"
                  value={formData.pickupBranch}
                  onChange={handleChange}
                  required
                  className="input-compact"
                >
                  <option value="">Select branch...</option>
                  {branches.map(branch => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name} - {branch.address.city}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group compact">
              <input
                type="text"
                name="pickupAddress"
                value={formData.pickupAddress}
                onChange={handleChange}
                required
                placeholder="Street Address *"
                className="input-compact"
                readOnly={formData.pickupType === 'branch' && formData.pickupBranch && !isEditMode}
              />
            </div>

            <div className="form-row-3">
              <input
                type="text"
                name="pickupCity"
                value={formData.pickupCity}
                onChange={handleChange}
                required
                placeholder="City *"
                className="input-compact"
                readOnly={formData.pickupType === 'branch' && formData.pickupBranch && !isEditMode}
              />
              <input
                type="text"
                name="pickupState"
                value={formData.pickupState}
                onChange={handleChange}
                required
                placeholder="State *"
                className="input-compact"
                readOnly={formData.pickupType === 'branch' && formData.pickupBranch && !isEditMode}
              />
              <input
                type="text"
                name="pickupZipCode"
                value={formData.pickupZipCode}
                onChange={handleChange}
                required
                placeholder="Zip *"
                className="input-compact"
                readOnly={formData.pickupType === 'branch' && formData.pickupBranch && !isEditMode}
              />
            </div>

            <div className="form-group compact">
              <input
                type="tel"
                name="pickupPhone"
                value={formData.pickupPhone}
                onChange={handleChange}
                placeholder="Pickup Phone (optional)"
                className="input-compact"
                readOnly={formData.pickupType === 'branch' && formData.pickupBranch && !isEditMode}
              />
            </div>

            <LocationPicker
              latitude={formData.pickupLatitude}
              longitude={formData.pickupLongitude}
              onLocationChange={handlePickupLocationChange}
              compact={true}
            />
          </div>

          {/* Delivery Information Column */}
          <div className="form-column">
            <h2 className="section-title compact">Delivery</h2>

            <div className="form-row-2">
              <input
                type="text"
                name="deliveryName"
                value={formData.deliveryName}
                onChange={handleChange}
                required
                placeholder="Recipient Name *"
                className="input-compact"
              />
              <input
                type="tel"
                name="deliveryPhone"
                value={formData.deliveryPhone}
                onChange={handleChange}
                placeholder="Phone"
                className="input-compact"
              />
            </div>

            <div className="form-group compact">
              <input
                type="text"
                name="deliveryAddress"
                value={formData.deliveryAddress}
                onChange={handleChange}
                required
                placeholder="Street Address *"
                className="input-compact"
              />
            </div>

            <div className="form-row-3">
              <input
                type="text"
                name="deliveryCity"
                value={formData.deliveryCity}
                onChange={handleChange}
                required
                placeholder="City *"
                className="input-compact"
              />
              <input
                type="text"
                name="deliveryState"
                value={formData.deliveryState}
                onChange={handleChange}
                required
                placeholder="State *"
                className="input-compact"
              />
              <input
                type="text"
                name="deliveryZipCode"
                value={formData.deliveryZipCode}
                onChange={handleChange}
                required
                placeholder="Zip *"
                className="input-compact"
              />
            </div>

            {/* Optional Delivery Location Map */}
            <div className="location-toggle-compact">
              <button
                type="button"
                className={`location-toggle-btn-compact ${showDeliveryMap ? 'active' : ''}`}
                onClick={() => {
                  if (showDeliveryMap) {
                    setFormData(prev => ({
                      ...prev,
                      deliveryLatitude: '',
                      deliveryLongitude: '',
                    }));
                  }
                  setShowDeliveryMap(!showDeliveryMap);
                }}
              >
                {showDeliveryMap ? 'Hide Map' : 'Set Location on Map (Optional)'}
              </button>
              {formData.deliveryLatitude && formData.deliveryLongitude && !showDeliveryMap && (
                <span className="coords-badge">
                  {formData.deliveryLatitude}, {formData.deliveryLongitude}
                </span>
              )}
            </div>

            {showDeliveryMap && (
              <LocationPicker
                latitude={formData.deliveryLatitude}
                longitude={formData.deliveryLongitude}
                onLocationChange={handleDeliveryLocationChange}
                compact={true}
              />
            )}

            <div className="form-group compact">
              <textarea
                name="deliveryInstructions"
                value={formData.deliveryInstructions}
                onChange={handleChange}
                rows="2"
                placeholder="Delivery instructions (optional)"
                className="input-compact textarea-compact"
              />
            </div>
          </div>
        </div>

        {/* Order Details & Actions - Single Row */}
        <div className="form-footer">
          <div className="order-options">
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="input-compact"
            >
              <option value="Low">Low Priority</option>
              <option value="Medium">Medium (Standard)</option>
              <option value="High">High (Urgent)</option>
              <option value="Priority order">Express</option>
            </select>
            <label className="signature-required-toggle" title="Driver must capture the recipient's signature at delivery">
              <input
                type="checkbox"
                name="signatureRequired"
                checked={formData.signatureRequired}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, signatureRequired: e.target.checked }))
                }
              />
              <span>Signature required</span>
            </label>
            <input
              type="text"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Additional notes..."
              className="input-compact notes-input"
            />
          </div>
          <div className="form-actions compact">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/tasks')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading
                ? (isEditMode ? 'Updating...' : 'Creating...')
                : (isEditMode ? 'Update Order' : 'Create Order')}
            </button>
          </div>
        </div>
      </form>

      {/* QR Code Modal (only for new orders) */}
      {showQRModal && !isEditMode && (
        <div className="qr-modal-overlay" onClick={handleCloseModal}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="qr-modal-close" onClick={handleCloseModal}>
              ✕
            </button>

            <h2 className="qr-modal-title">✅ Order Created Successfully!</h2>
            <p className="qr-modal-subtitle">
              Job ID: <strong>{createdJobId}</strong> | QR Code generated
            </p>

            <div className="qr-modal-image-container">
              {qrCodeUrl ? (
                <img src={qrCodeUrl} alt="QR Code" className="qr-modal-image" />
              ) : (
                <p>Loading QR code...</p>
              )}
              {qrCode && (
                <p className="qr-code-text">{qrCode}</p>
              )}
            </div>

            {/* Public Tracking Link */}
            {createdJobId && (
              <div className="qr-tracking-link-section" style={{
                marginTop: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#666', fontWeight: '600' }}>
                  📍 Public Tracking Link
                </p>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <input
                    type="text"
                    value={`${window.location.origin}/track/${createdJobId}`}
                    readOnly
                    style={{
                      flex: '1',
                      minWidth: '250px',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      fontFamily: 'monospace'
                    }}
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/track/${createdJobId}`);
                      alert('Tracking link copied to clipboard!');
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#6366f1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}
                  >
                    📋 Copy Link
                  </button>
                </div>
              </div>
            )}

            <div className="qr-modal-actions">
              <button className="btn-secondary" onClick={() => window.print()}>
                🖨️ Print QR Code
              </button>
              <button className="btn-primary" onClick={handleCloseModal}>
                📋 View All Orders
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddTaskEnhanced;
